import React, { useMemo, useCallback, useRef, useState, useEffect } from "react";
import Icon1 from "@assets/icons/Icon";
import { usePlanningStore, getCategories, COMPACTION_CONFIG } from "@store/planningStore";
import { AppointmentCategory, Appointment, CategoryConfig } from "../../types/garage/planning";
import { snapToGrid, formatTime, getBookingHours, getClosedIntervals, closedSegmentsInHour } from "@utils/timeGrid";
import OrdreModal from "@components/modals/OrdreModal";
import LocationModal from "@components/modals/LocationModal";
import RestitutionChangeConfirmModal from "@components/modals/RestitutionChangeConfirmModal";
import QuickAppointmentModal from "@components/modals/QuickAppointmentModal";
import InternalAppointmentModal from "@components/modals/InternalAppointmentModal";
import { findOrdreForPlate, buildOrdreFromAppointment } from "@store/ordreStore";
import { ordresApi } from "@services/api/ordres";
import { vehiclesApi } from "@services/api/vehicles";
import { useDataStore } from "@store/user_org";
import { isOwnerOrAdmin } from "@utils/permissions";
import { useFullClosures, isDateClosed } from "@utils/closedDates";
import { receptionSlotTaken } from "@utils/receptionOverlap";
import { showNotification } from "context/notification.service";
import { ClosedDayOverlay } from "@components/ui/ClosedDayOverlay";
import { getReceptionKind } from "types/org";
import type { Ordre } from "types/ordre";
import type { VehicleReservation } from "types/garage/vehicules";
import { PlanningContextMenu, ContextMenuState } from "@components/ui/PlanningContextMenu";
import AppointmentTooltip from "@components/ui/AppointmentTooltip";
import { getStatusColor } from "@components/ui/dropdown/OrdreStatusDropdown";

const HOURS_START = 7;
const HOURS_END = 20;
const SNAP_HOUR = 0.125;

const FALLBACK_CONFIG: CategoryConfig = { id: "rdv_divers", label: "?", color: "#818CF8", bgColor: "#E0E6FF", borderColor: "#CCD0FF", barColor: "#A5B4FC" };

interface DragPreview {
  apptId: string;
  startHour: number;
  durationHours: number;
  category: AppointmentCategory;
}

const CLICK_THRESHOLD = 10; // px — if pointer moves less, treat as click

export function TimeGridWithAppointments({ externalPreviews }: { externalPreviews?: DragPreview[] } = {}) {
  const getFilteredAppointments = usePlanningStore(
    (s) => s.getFilteredAppointments,
  );
  const activeCategories = usePlanningStore((s) => s.activeCategories);
  const updateAppointment = usePlanningStore((s) => s.updateAppointment);
  const compaction = usePlanningStore((s) => s.compaction);
  const HOUR_BLOCK_HEIGHT = COMPACTION_CONFIG[compaction].hourBlockHeight;
  const SNAP_PX = COMPACTION_CONFIG[compaction].snapPx;
  // Grid cells must scale with the hour block height, otherwise the inner
  // grid stays fixed (4×32=128px) and no longer fills the block in compact/
  // spread modes — leaving irregular gaps. One quarter = 1/4 of the hour.
  const QUARTER_CELL_HEIGHT = HOUR_BLOCK_HEIGHT / 4;
  const HALF_CELL_HEIGHT = HOUR_BLOCK_HEIGHT / 8;
  const appointments = getFilteredAppointments();

  const userData = useDataStore((s) => s.userData);
  const orgData = useDataStore((s) => s.orgData);
  const canMoveAppt = isOwnerOrAdmin(userData);
  const selectedDate = usePlanningStore((s) => s.selectedDate);
  const closedDates = useFullClosures();
  const highlightOrdreId = usePlanningStore((s) => s.highlightOrdreId);

  const allCategories = useMemo(() => getCategories(orgData), [orgData]);
  const gridCategories = useMemo(() => allCategories.map((c) => c.id), [allCategories]);
  const getCategoryConfig = useCallback(
    (id: AppointmentCategory) => allCategories.find((c) => c.id === id) || FALLBACK_CONFIG,
    [allCategories],
  );
  const [selectedOrdre, setSelectedOrdre] = useState<Ordre | null>(null);
  const [selectedReservation, setSelectedReservation] = useState<VehicleReservation | null>(null);
  const [selectedInternal, setSelectedInternal] = useState<Appointment | null>(null);
  const [hoveredApptId, setHoveredApptId] = useState<string | null>(null);
  // Delay hiding the hover tooltip so the user can move into it (to click the status chip)
  const tooltipHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showTooltip = useCallback((id: string) => {
    if (tooltipHideTimer.current) clearTimeout(tooltipHideTimer.current);
    setHoveredApptId(id);
  }, []);
  const hideTooltipSoon = useCallback(() => {
    if (tooltipHideTimer.current) clearTimeout(tooltipHideTimer.current);
    tooltipHideTimer.current = setTimeout(() => setHoveredApptId(null), 150);
  }, []);
  // Once the user interacts with the tooltip (clicks the status chip) it becomes
  // "pinned": it no longer follows hover and only closes on an outside click — so
  // changing the status keeps the tooltip open with the new value visible.
  const [pinnedApptId, setPinnedApptId] = useState<string | null>(null);
  const clickStartRef = useRef<{ x: number; y: number } | null>(null);
  const [quickCreate, setQuickCreate] = useState<{ date: string; startHour: number; category: AppointmentCategory } | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);

  // Close a pinned tooltip (and its status menu) only when clicking outside both.
  useEffect(() => {
    if (!pinnedApptId) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest("[data-appt-tooltip]") || t.closest("[data-planning-context-menu]")) return;
      setPinnedApptId(null);
      setContextMenu(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [pinnedApptId]);

  const [pendingMoveConfirm, setPendingMoveConfirm] = useState<{
    apptId: string;
    updates: { startHour: number; durationHours: number; category: AppointmentCategory };
  } | null>(null);
  const [restitutionSaving, setRestitutionSaving] = useState(false);
  const [pendingRestitutionMove, setPendingRestitutionMove] = useState<{
    appt: Appointment;
    updates: { startHour: number; durationHours: number; category: AppointmentCategory };
    oldSeconds?: number;
    newSeconds: number;
  } | null>(null);
  const [receptionSaving, setReceptionSaving] = useState(false);
  const [pendingReceptionMove, setPendingReceptionMove] = useState<{
    appt: Appointment;
    updates: { startHour: number; durationHours: number; category: AppointmentCategory };
    oldSeconds?: number;
    newSeconds: number;
  } | null>(null);

  const visibleCategories = gridCategories.filter((c) =>
    activeCategories.includes(c),
  );
  const hours = Array.from(
    { length: HOURS_END - HOURS_START },
    (_, i) => HOURS_START + i,
  );

  // Closed periods (before opening, lunch break, after closing) → greyed out on the grid.
  const closedIntervals = useMemo(
    () => getClosedIntervals(getBookingHours(orgData?.config)),
    [orgData?.config],
  );

  const byCategory = useMemo(() => {
    const map: Record<string, Appointment[]> = {};
    for (const cat of gridCategories) map[cat] = [];
    for (const a of appointments) {
      if (a.allDay) continue; // all-day blocks live in the top banner
      if (map[a.category]) map[a.category].push(a);
    }
    return map;
  }, [appointments]);

  // All-day blocks covering the selected day → stacked rows for the top banner.
  const allAppointments = usePlanningStore((s) => s.appointments);
  const searchQuery = usePlanningStore((s) => s.searchQuery);
  const dayAllDay = useMemo(() => {
    return allAppointments.filter(
      (a) =>
        a.allDay &&
        activeCategories.includes(a.category) &&
        a.date <= selectedDate &&
        (a.endDate || a.date) >= selectedDate &&
        (searchQuery === "" ||
          a.clientName.toLowerCase().includes(searchQuery.toLowerCase())),
    );
  }, [allAppointments, activeCategories, selectedDate, searchQuery]);

  // Compute overlap layout per category: { colIndex, totalCols } for each appointment
  const overlapLayout = useMemo(() => {
    const layout: Record<string, { colIndex: number; totalCols: number }> = {};
    for (const cat of gridCategories) {
      const appts = byCategory[cat];
      if (!appts.length) continue;
      const sorted = [...appts].sort(
        (a, b) => a.startHour - b.startHour || b.durationHours - a.durationHours,
      );
      const OVERLAP_EPS = 0.034; // ~2 min tolerance for floating-point end/start match
      let clusterStart = 0;
      let maxEnd = 0;
      for (let i = 0; i <= sorted.length; i++) {
        const isEnd = i === sorted.length || (i > 0 && sorted[i].startHour >= maxEnd - OVERLAP_EPS);
        if (isEnd && i > clusterStart) {
          const columns: Appointment[][] = [];
          for (let k = clusterStart; k < i; k++) {
            const appt = sorted[k];
            let placed = false;
            for (let col = 0; col < columns.length; col++) {
              const lastInCol = columns[col][columns[col].length - 1];
              if (lastInCol.startHour + lastInCol.durationHours <= appt.startHour + OVERLAP_EPS) {
                columns[col].push(appt);
                layout[appt.id] = { colIndex: col, totalCols: 0 };
                placed = true;
                break;
              }
            }
            if (!placed) {
              layout[appt.id] = { colIndex: columns.length, totalCols: 0 };
              columns.push([appt]);
            }
          }
          const totalCols = columns.length;
          for (let k = clusterStart; k < i; k++) {
            layout[sorted[k].id].totalCols = totalCols;
          }
          clusterStart = i;
        }
        if (i < sorted.length) {
          maxEnd = Math.max(maxEnd, sorted[i].startHour + sorted[i].durationHours);
        }
      }
    }
    return layout;
  }, [byCategory]);

  const pendingDragRef = useRef<{
    appt: Appointment;
    mode: "move" | "resize";
    startX: number;
    startY: number;
  } | null>(null);
  const dragRef = useRef<{
    apptId: string;
    mode: "move" | "resize";
    startY: number;
    origStartHour: number;
    origDuration: number;
    origCategory: AppointmentCategory;
  } | null>(null);
  const [preview, setPreview] = useState<DragPreview | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, appt: Appointment, mode: "move" | "resize") => {
      if (e.button === 2) return; // ignore right-click (context menu)
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);

      clickStartRef.current = { x: e.clientX, y: e.clientY };

      // Two-phase drag: store pending, only activate when threshold exceeded
      pendingDragRef.current = {
        appt,
        mode,
        startX: e.clientX,
        startY: e.clientY,
      };
    },
    [],
  );

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    // Phase 1: check if pending drag should activate
    const pending = pendingDragRef.current;
    if (pending && !dragRef.current) {
      const dx = Math.abs(e.clientX - pending.startX);
      const dy = Math.abs(e.clientY - pending.startY);
      if (dx >= CLICK_THRESHOLD || dy >= CLICK_THRESHOLD) {
        const { appt, mode } = pending;
        dragRef.current = {
          apptId: appt.id,
          mode,
          startY: pending.startY,
          origStartHour: appt.startHour,
          origDuration: appt.durationHours,
          origCategory: appt.category,
        };
        setPreview({
          apptId: appt.id,
          startHour: appt.startHour,
          durationHours: appt.durationHours,
          category: appt.category,
        });
        pendingDragRef.current = null;
      } else {
        return;
      }
    }

    // Phase 2: active drag
    const d = dragRef.current;
    if (!d) return;

    const deltaY = e.clientY - d.startY;
    const snapDelta = Math.round(deltaY / SNAP_PX);

    if (d.mode === "move") {
      const newStart = snapToGrid(
        d.origStartHour + snapDelta * SNAP_HOUR,
        SNAP_HOUR,
      );
      const clamped = Math.max(
        HOURS_START,
        Math.min(newStart, HOURS_END - d.origDuration),
      );

      let newCategory = d.origCategory;
      if (scrollRef.current) {
        const cols =
          scrollRef.current.querySelectorAll<HTMLElement>("[data-cat-col]");
        cols.forEach((el) => {
          const rect = el.getBoundingClientRect();
          if (e.clientX >= rect.left && e.clientX < rect.right) {
            newCategory = el.getAttribute(
              "data-cat-col",
            ) as AppointmentCategory;
          }
        });
      }

      setPreview({
        apptId: d.apptId,
        startHour: clamped,
        durationHours: d.origDuration,
        category: newCategory,
      });
    } else {
      const newDuration = snapToGrid(
        d.origDuration + snapDelta * SNAP_HOUR,
        SNAP_HOUR,
      );
      const clamped = Math.max(
        SNAP_HOUR,
        Math.min(newDuration, HOURS_END - d.origStartHour),
      );
      setPreview({
        apptId: d.apptId,
        startHour: d.origStartHour,
        durationHours: clamped,
        category: d.origCategory,
      });
    }
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    const pending = pendingDragRef.current;
    const d = dragRef.current;
    const p = preview;
    const start = clickStartRef.current;

    pendingDragRef.current = null;

    // Check if this was a click (no significant movement)
    const isClick = start &&
      Math.abs(e.clientX - start.x) < CLICK_THRESHOLD &&
      Math.abs(e.clientY - start.y) < CLICK_THRESHOLD;

    // Click: open the appointment (from pending move or active drag that didn't move)
    const clickApptId = pending?.appt.id || (d?.mode === "move" ? d.apptId : null);
    if (isClick && clickApptId) {
      const appt = appointments.find((a) => a.id === clickApptId);
      if (appt) {
        if (appt.reservation_id) {
          vehiclesApi.getReservation(appt.reservation_id).then(setSelectedReservation).catch(() => {});
        } else if (appt.ordre_id) {
          // Fetch fresh ordre from API so dragged times are reflected
          ordresApi.get(appt.ordre_id).then((ordre) => setSelectedOrdre(ordre)).catch(() => {
            const fallback = findOrdreForPlate(appt.plate);
            if (fallback) setSelectedOrdre(fallback);
            else if (appt.plate) setSelectedOrdre(buildOrdreFromAppointment(appt));
            else setSelectedInternal(appt);
          });
        } else if (appt.plate) {
          setSelectedOrdre(findOrdreForPlate(appt.plate) || buildOrdreFromAppointment(appt));
        } else {
          setSelectedInternal(appt);
        }
      }
    } else if (d && p) {
      // Non-managers cannot move appointments
      if (!canMoveAppt) {
        dragRef.current = null;
        clickStartRef.current = null;
        setPreview(null);
        return;
      }
      const appt = appointments.find((a) => a.id === d.apptId);
      // Block a move onto a slot already taken on the same reception column.
      const targetDate = appt?.date || selectedDate;
      if (
        appt &&
        receptionSlotTaken(appointments, {
          category: p.category,
          date: targetDate,
          startHour: p.startHour,
          durationHours: p.durationHours,
          excludeId: d.apptId,
        })
      ) {
        showNotification({ type: "error", message: "Créneau déjà occupé sur cette réception" });
        dragRef.current = null;
        clickStartRef.current = null;
        setPreview(null);
        return;
      }
      if (appt?.ordre_id) {
        // Ordre-linked move (reception or restitution) — confirm + offer to notify client
        const selectedDate = usePlanningStore.getState().selectedDate;
        const apptDate = appt.date || selectedDate;
        const oldDate = new Date(`${apptDate}T00:00:00`);
        oldDate.setHours(Math.floor(appt.startHour), Math.floor((appt.startHour % 1) * 60 + 1e-9));
        const newDate = new Date(`${apptDate}T00:00:00`);
        newDate.setHours(Math.floor(p.startHour), Math.floor((p.startHour % 1) * 60 + 1e-9));
        const move = {
          appt,
          updates: { startHour: p.startHour, durationHours: p.durationHours, category: p.category },
          oldSeconds: Math.floor(oldDate.getTime() / 1000),
          newSeconds: Math.floor(newDate.getTime() / 1000),
        };
        if (appt.type === "restitution") setPendingRestitutionMove(move);
        else setPendingReceptionMove(move);
      } else {
        // Manager move — show confirmation modal
        setPendingMoveConfirm({
          apptId: d.apptId,
          updates: { startHour: p.startHour, durationHours: p.durationHours, category: p.category },
        });
      }
    }
    dragRef.current = null;
    clickStartRef.current = null;
    setPreview(null);
  }, [preview, updateAppointment, appointments]);

  const headerHeight = 28;
  const hourLabelWidth = 32;
  const minuteColWidth = 24;

  return (
    <div
      className="relative"
      style={{
        border: "1px solid #D9D9D9",
        borderRadius: 10,
        overflow: "hidden",
      }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      {/* Column headers */}
      <div className="flex flex-row" style={{ height: headerHeight }}>
        <div
          style={{
            width: hourLabelWidth + minuteColWidth + 1,
            borderRight: "1px solid #D9D9D9",
            borderBottom: "1px solid #D9D9D9",
          }}
        />
        {visibleCategories.map((catId) => {
          const cat = getCategoryConfig(catId);
          return (
            <div
              key={catId}
              className="flex-1 flex items-center justify-center text-sm font-medium whitespace-nowrap"
              style={{
                borderRight: "1px solid #D9D9D9",
                borderBottom: "1px solid #D9D9D9",
                background: "#FFFFFF",
                color: cat.color,
              }}
            >
              {cat.label === "Location" ? "Location RAC" : cat.label}
            </div>
          );
        })}
      </div>

      {/* All-day banner (Outlook-style) — full-width bars for the selected day */}
      {dayAllDay.length > 0 && (
        <div className="flex flex-row" style={{ borderBottom: "1px solid #D9D9D9", background: "#FFF" }}>
          <div
            className="flex items-center justify-center"
            style={{
              width: hourLabelWidth + minuteColWidth + 1,
              borderRight: "1px solid #D9D9D9",
              flexShrink: 0,
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: 10,
              color: "#A1A1A1",
            }}
          >
            Jour.
          </div>
          <div className="flex-1 flex flex-col" style={{ gap: 2, padding: 3 }}>
            {dayAllDay.map((appt) => {
              const cfg = getCategoryConfig(appt.category);
              return (
                <div
                  key={appt.id}
                  data-appt
                  onClick={() => setSelectedInternal(appt)}
                  title={appt.clientName}
                  className="flex items-center"
                  style={{
                    height: 20,
                    background: cfg.bgColor,
                    border: `1px solid ${cfg.borderColor}`,
                    borderRadius: 4,
                    padding: "0 6px",
                    cursor: "pointer",
                    overflow: "hidden",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: cfg.color,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {appt.clientName}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Scrollable body */}
      <div ref={scrollRef} className="relative">
        {/* Org closure day indicator */}
        {isDateClosed(selectedDate, closedDates) && <ClosedDayOverlay />}
        <div className="flex flex-col">
          {hours.map((h) => (
            <div
              key={h}
              className="flex flex-row"
              style={{
                height: HOUR_BLOCK_HEIGHT,
                borderBottom: "2px solid #BDBDBD",
              }}
            >
              <div
                className="flex items-start"
                style={{
                  width: hourLabelWidth,
                  paddingLeft: 7,
                  paddingTop: 2,
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: 12,
                  color: "#A1A1A1",
                  flexShrink: 0,
                }}
              >
                {h}h
              </div>

              <div
                className="flex flex-col"
                style={{
                  width: minuteColWidth,
                  borderLeft: "1px solid #D9D9D9",
                  borderRight: "1px solid #D9D9D9",
                  flexShrink: 0,
                }}
              >
                {["00", "15", "30", "45"].map((m) => (
                  <div
                    key={m}
                    className="flex items-center justify-center"
                    style={{
                      height: QUARTER_CELL_HEIGHT,
                      borderBottom: "1px solid #D9D9D9",
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      fontSize: 12,
                      color: "#A1A1A1",
                    }}
                  >
                    {m}
                  </div>
                ))}
              </div>

              {/* Category columns */}
              {visibleCategories.map((catId) => {
                const colAppts = (byCategory[catId] || []).filter(
                  (a) => a.startHour >= h && a.startHour < h + 1,
                );

                const ghostInCell =
                  preview &&
                  preview.category === catId &&
                  preview.startHour >= h &&
                  preview.startHour < h + 1
                    ? preview
                    : null;

                const extGhosts = (externalPreviews || []).filter(
                  (ep) => ep.category === catId && ep.startHour >= h && ep.startHour < h + 1,
                );

                return (
                  <div
                    key={catId}
                    data-cat-col={catId}
                    className="flex-1 relative cursor-pointer"
                    style={{ borderRight: "1px solid #D9D9D9" }}
                    onClick={(e) => {
                      // Only trigger if clicking empty space (not an appointment)
                      if ((e.target as HTMLElement).closest("[data-appt]")) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const relY = e.clientY - rect.top;
                      const clickedHour = snapToGrid(h + (relY / HOUR_BLOCK_HEIGHT), SNAP_HOUR);
                      setQuickCreate({ date: selectedDate, startHour: clickedHour, category: catId });
                    }}
                  >
                    {[0, 1, 2, 3].map((q) => (
                      <div
                        key={q}
                        style={{
                          height: QUARTER_CELL_HEIGHT,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div
                          style={{
                            height: HALF_CELL_HEIGHT,
                            borderBottom: "1px solid #D9D9D9",
                          }}
                        />
                        <div
                          style={{
                            height: HALF_CELL_HEIGHT,
                            background: "#F4F4F4",
                            // Last quarter carries the prominent hour separator so the
                            // bold hour line shows in the central content, not only in
                            // the left time column.
                            borderBottom: q === 3 ? "2px solid #BDBDBD" : "1px solid #D9D9D9",
                          }}
                        />
                      </div>
                    ))}

                    {/* Closed periods (outside opening hours / lunch break) — greyed out */}
                    {closedSegmentsInHour(h, closedIntervals).map(([cs, ce], idx) => (
                      <div
                        key={`closed-${idx}`}
                        className="absolute left-0 right-0 pointer-events-none"
                        style={{
                          top: ((cs - h) / SNAP_HOUR) * SNAP_PX,
                          height: ((ce - cs) / SNAP_HOUR) * SNAP_PX,
                          background:
                            "repeating-linear-gradient(45deg, rgba(120,120,120,0.13), rgba(120,120,120,0.13) 6px, rgba(120,120,120,0.20) 6px, rgba(120,120,120,0.20) 12px)",
                          zIndex: 1,
                        }}
                      />
                    ))}

                    {/* Ghost preview (drag) */}
                    {ghostInCell &&
                      (() => {
                        const config = getCategoryConfig(ghostInCell.category);
                        const topPx =
                          ((ghostInCell.startHour - h) / SNAP_HOUR) * SNAP_PX;
                        const heightPx =
                          (ghostInCell.durationHours / SNAP_HOUR) * SNAP_PX;
                        return (
                          <div
                            className="absolute left-0 right-0 flex flex-col items-center justify-center pointer-events-none"
                            style={{
                              top: topPx,
                              height: Math.max(heightPx, 20),
                              background: config.bgColor,
                              border: `2px dashed ${config.color}`,
                              borderRadius: 5,
                              margin: "0 2px",
                              zIndex: 40,
                              opacity: 0.7,
                            }}
                          >
                            <span
                              className="text-h4 font-medium"
                              style={{
                                color: config.color,
                                lineHeight: "100%",
                              }}
                            >
                              {formatTime(ghostInCell.startHour)} –{" "}
                              {formatTime(
                                ghostInCell.startHour +
                                  ghostInCell.durationHours,
                              )}
                            </span>
                          </div>
                        );
                      })()}

                    {/* External previews (debug) */}
                    {extGhosts.map((eg, idx) => {
                      const config = getCategoryConfig(eg.category);
                      const topPx = ((eg.startHour - h) / SNAP_HOUR) * SNAP_PX;
                      const heightPx = (eg.durationHours / SNAP_HOUR) * SNAP_PX;
                      return (
                        <div
                          key={`ext-${idx}`}
                          className="absolute left-0 right-0 flex flex-col items-center justify-center pointer-events-none"
                          style={{
                            top: topPx,
                            height: Math.max(heightPx, 20),
                            background: config.bgColor,
                            border: `2px dashed ${config.color}`,
                            borderRadius: 5,
                            margin: "0 2px",
                            zIndex: 40,
                            opacity: 0.7,
                          }}
                        >
                          <span
                            className="text-h4 font-medium"
                            style={{ color: config.color, lineHeight: "100%" }}
                          >
                            {formatTime(eg.startHour)} – {formatTime(eg.startHour + eg.durationHours)}
                          </span>
                        </div>
                      );
                    })}

                    {/* Actual appointments */}
                    {colAppts.map((appt) => {
                      const config = getCategoryConfig(appt.category);
                      const topPx =
                        ((appt.startHour - h) / SNAP_HOUR) * SNAP_PX;
                      const heightPx =
                        (appt.durationHours / SNAP_HOUR) * SNAP_PX;
                      const isDragSource = preview?.apptId === appt.id;
                      const isHovered = hoveredApptId === appt.id;
                      const isPinned = pinnedApptId === appt.id;
                      const elevated = isHovered || isPinned;
                      // Pinned tooltips win; while one is pinned, hover tooltips on
                      // other events are suppressed to avoid two open at once.
                      const tooltipVisible = (isPinned || (isHovered && pinnedApptId === null)) && !preview;
                      const apptStatus = appt.status;
                      const isHighlighted = highlightOrdreId != null && appt.ordre_id === highlightOrdreId;
                      const ol = overlapLayout[appt.id] || { colIndex: 0, totalCols: 1 };
                      const widthPct = `calc(${100 / ol.totalCols}% - 4px)`;
                      const leftPct = `calc(${(ol.colIndex / ol.totalCols) * 100}% + 2px)`;
                      return (
                        <div
                          key={appt.id}
                          data-appt
                          className={`absolute flex flex-col${isHighlighted ? " highlight-pulse" : ""}`}
                          style={{
                            top: topPx,
                            left: leftPct,
                            width: widthPct,
                            height: Math.max(heightPx, 14),
                            background: config.bgColor,
                            border: isHighlighted ? "2px solid #6366F1" : `1px solid ${config.borderColor}`,
                            borderLeft: apptStatus ? `5px solid ${getStatusColor(apptStatus)}` : undefined,
                            boxShadow: isHighlighted
                              ? "0px 0px 12px rgba(99, 102, 241, 0.5)"
                              : elevated
                              ? "0px 4px 12px rgba(0, 0, 0, 0.25)"
                              : "none",
                            borderRadius: 5,
                            padding: heightPx < 28 ? "1px 4px" : "3px 4px",
                            overflow: "hidden",
                            zIndex: isDragSource ? 5 : elevated ? 15 : 10,
                            cursor: canMoveAppt ? "grab" : "pointer",
                            userSelect: "none",
                            opacity: isDragSource ? 0.35 : 1,
                            transition: isDragSource
                              ? "opacity 0.15s"
                              : "box-shadow 0.15s",
                          }}
                          onPointerDown={(e) =>
                            handlePointerDown(e, appt, "move")
                          }
                          onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!appt.ordre_id || !apptStatus) return;
                            setContextMenu({ x: e.clientX, y: e.clientY, ordreId: appt.ordre_id, currentStatus: apptStatus, num: appt.num });
                          }}
                          onMouseEnter={() => showTooltip(appt.id)}
                          onMouseLeave={hideTooltipSoon}
                        >
                          {/* Hover tooltip (becomes pinned once the status chip is clicked) */}
                          {tooltipVisible && (
                            <AppointmentTooltip
                              appt={appt}
                              appointments={appointments}
                              status={apptStatus}
                              fallbackDate={selectedDate}
                              onMouseEnter={() => showTooltip(appt.id)}
                              onMouseLeave={isPinned ? () => {} : hideTooltipSoon}
                              onEditStatus={(anchor) => {
                                if (!appt.ordre_id || !apptStatus) return;
                                setPinnedApptId(appt.id);
                                setContextMenu({ x: anchor.left, y: anchor.bottom + 4, ordreId: appt.ordre_id, currentStatus: apptStatus, num: appt.num });
                              }}
                            />
                          )}
                          <div
                            className="flex flex-row items-center"
                            style={{ gap: 3, minWidth: 0, overflow: "hidden", flex: 1 }}
                          >
                            {appt.hasLoaner && (
                              <svg width="13" height="13" viewBox="0 0 24 24" fill={config.color} style={{ flexShrink: 0 }} aria-label="Véhicule de prêt">
                                <path d="M5 11l1.4-3.3C6.7 6.7 7.5 6 8.5 6h7c1 0 1.8.7 2.1 1.7L19 11l1.3.5c.4.2.7.6.7 1V16c0 .6-.4 1-1 1h-1c-.6 0-1-.4-1-1H6c0 .6-.4 1-1 1H4c-.6 0-1-.4-1-1v-3.5c0-.4.3-.8.7-1L5 11zm2 4a1 1 0 100-2 1 1 0 000 2zm10 0a1 1 0 100-2 1 1 0 000 2z" />
                              </svg>
                            )}
                            {(appt.hasCarIcon || appt.hasMailIcon) && (
                              <Icon1
                                name={appt.type === "restitution" ? "car-restitution" : "car-reception"}
                                size={appt.type === "restitution" ? 12 : 13.2}
                                padding={appt.type === "restitution" ? "0px 0px 0px 2px" : "0px"}
                                color={config.color}
                              />
                            )}
                            <span
                              className="font-medium"
                              style={{
                                fontSize: heightPx < 28 ? 10 : 12,
                                lineHeight: "110%",
                                color: config.color,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                              }}
                            >
                              {appt.clientName}
                              {heightPx >= 28 && appt.plate ? ` - ${appt.plate}` : ""}
                            </span>
                          </div>
                          {heightPx >= 24 && (
                            <div
                              className="absolute bottom-0 left-0 right-0 flex justify-center items-center"
                              style={{ height: 8, cursor: "ns-resize" }}
                              onPointerDown={(e) => {
                                e.stopPropagation();
                                handlePointerDown(e, appt, "resize");
                              }}
                            >
                              <div
                                style={{
                                  width: 20,
                                  height: 2,
                                  borderRadius: 1,
                                  background: config.color,
                                  opacity: 0.4,
                                }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      {selectedOrdre && (
        <OrdreModal
          ordre={selectedOrdre}
          onClose={() => setSelectedOrdre(null)}
          onSave={() => setSelectedOrdre(null)}
          onDelete={isOwnerOrAdmin(userData) ? () => setSelectedOrdre(null) : undefined}
        />
      )}
      {selectedReservation && (
        <LocationModal
          reservation={selectedReservation}
          onClose={() => setSelectedReservation(null)}
          onSave={() => setSelectedReservation(null)}
        />
      )}
      {pendingRestitutionMove && (
        <RestitutionChangeConfirmModal
          oldSeconds={pendingRestitutionMove.oldSeconds}
          newSeconds={pendingRestitutionMove.newSeconds}
          onClose={() => setPendingRestitutionMove(null)}
          saving={restitutionSaving}
          onConfirm={async (comment, notify) => {
            setRestitutionSaving(true);
            try {
              const { appt, updates } = pendingRestitutionMove;
              updateAppointment(appt.id, updates);
              if (appt.ordre_id) {
                await ordresApi.notifyRestitutionChange(appt.ordre_id, {
                  old_date_seconds: pendingRestitutionMove.oldSeconds,
                  new_date_seconds: pendingRestitutionMove.newSeconds,
                  comment: comment || undefined,
                  notify,
                });
              }
              setPendingRestitutionMove(null);
            } catch (e) {
              console.error("Error notifying restitution change:", e);
            } finally {
              setRestitutionSaving(false);
            }
          }}
        />
      )}
      {pendingReceptionMove && (
        <RestitutionChangeConfirmModal
          kind="reception"
          oldSeconds={pendingReceptionMove.oldSeconds}
          newSeconds={pendingReceptionMove.newSeconds}
          onClose={() => setPendingReceptionMove(null)}
          saving={receptionSaving}
          onConfirm={async (comment, notify) => {
            setReceptionSaving(true);
            try {
              const { appt, updates } = pendingReceptionMove;
              updateAppointment(appt.id, updates);
              if (appt.ordre_id) {
                await ordresApi.notifyReceptionChange(appt.ordre_id, {
                  old_date_seconds: pendingReceptionMove.oldSeconds,
                  new_date_seconds: pendingReceptionMove.newSeconds,
                  comment: comment || undefined,
                  notify,
                });
              }
              setPendingReceptionMove(null);
            } catch (e) {
              console.error("Error notifying reception change:", e);
            } finally {
              setReceptionSaving(false);
            }
          }}
        />
      )}
      {pendingMoveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl p-5 w-[340px] flex flex-col gap-4 shadow-xl">
            <h3 className="text-[15px] font-semibold text-neutral-800 m-0">Déplacer ce rendez-vous ?</h3>
            <p className="text-[13px] text-neutral-500 m-0">
              Vous êtes sur le point de modifier l'horaire de ce rendez-vous.
            </p>
            <div className="flex flex-row gap-2 justify-end">
              <button
                onClick={() => setPendingMoveConfirm(null)}
                className="px-4 py-2 rounded-lg text-[13px] font-medium text-neutral-600 bg-neutral-100 hover:bg-neutral-200 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  updateAppointment(pendingMoveConfirm.apptId, pendingMoveConfirm.updates);
                  setPendingMoveConfirm(null);
                }}
                className="px-4 py-2 rounded-lg text-[13px] font-medium text-white bg-[#5C6BFF] hover:bg-[#4B5AEE] transition-colors"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
      {quickCreate && (() => {
        const rec = (orgData?.receptions ?? []).find((r) => r.id === quickCreate.category);
        const k = rec ? getReceptionKind(rec) : "reception";
        const defaultType =
          k === "location" || k === "pret" ? "location" : k === "divers" ? "interne" : "repair";
        return (
          <QuickAppointmentModal
            date={quickCreate.date}
            startHour={quickCreate.startHour}
            category={quickCreate.category}
            defaultType={defaultType}
            onClose={() => setQuickCreate(null)}
            onCreated={(result) => {
              if (result.type === "repair") {
                ordresApi.get(result.ordreId).then((ordre) => setSelectedOrdre(ordre)).catch(() => {});
              } else if (result.type === "location") {
                setSelectedReservation(result.reservation);
              }
              // Internal blocks just close the modal — they already show in the planning.
            }}
          />
        );
      })()}
      {selectedInternal && (
        <InternalAppointmentModal
          appointment={selectedInternal}
          onClose={() => setSelectedInternal(null)}
        />
      )}
      {contextMenu && (
        <PlanningContextMenu menu={contextMenu} onClose={() => setContextMenu(null)} />
      )}
    </div>
  );
}
